<script setup>
    import { ref, reactive, computed, onMounted } from 'vue'
    import NavigationBar from '../components/NavigationBar.vue'
    import HeaderComponent from '../components/HeaderComponent.vue'
    import { Bar } from 'vue-chartjs'
    import CallApi  from '../utils/callApi.js'
    import Cognito  from '../utils/cognito.js'
    import dayjs from 'dayjs';

    import {
      Chart as ChartJS,
      Title,
      Tooltip,
      Legend,
      BarElement,
      CategoryScale,
      LinearScale
    } from 'chart.js'

    ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)
    
    const nowDate = new Date()

    // 月間リクエスト集計期間 日付設定
    const createTotalRequestDate = () => {
        const year = nowDate.getFullYear()
        const month = nowDate.getMonth() + 1

        const yyyy = year.toString()
        const mm = String(month).padStart(2, "0")

        const lastDay = new Date(year, month, 0)
            .getDate()
            .toString()
            .padStart(2, "0")

        return {
            totalRequestDateY: yyyy,
            totalRequestDateM: mm,
            totalRequestStartDateD: "01",
            totalRequestEndDateD: lastDay
        }
    }

    // 月間リクエスト数
    const monthReqInfo = ref([])

    // 月間リクエスト集計期間
    const totalRequestDate = reactive(createTotalRequestDate())

    // 契約情報
    const contractInfo = reactive({
        // 契約開始期間
        contractStartDate: "",
        // 契約終了期間
        contractEndDate: "",
        // プラン名
        contractPlanName: "",
        // 月額料金
        monthPrice: 0,
        // 登録人数上限
        limitRegister: 0,
        // リクエスト上限数
        limitReqNum: 0
    })
    // パスワード変更情報
    const changePassInfo = reactive({
        // 現在のパスワード
        currentPass: "",
        // 新しいパスワード1
        newPass1: "",
        // 新しいパスワード2
        newPass2: ""
    })

    const isLoading = ref(false); // ローディング画面表示フラグ

    // 今月の月初/月末の日付を取得する
    const getTotalRequestStartDate = computed(() => {

        return `${totalRequestDate.totalRequestDateY}/${totalRequestDate.totalRequestDateM}/${totalRequestDate.totalRequestStartDateD}`
    })
    const getTotalRequestEndDate = computed(() => {
        
        return `${totalRequestDate.totalRequestDateY}/${totalRequestDate.totalRequestDateM}/${totalRequestDate.totalRequestEndDateD}`
    })

    // 半年分のリクエスト回数を取得する
    const getTotalRequest = async() => {
        try {
            const startDate = new Date(nowDate.getFullYear(), nowDate.getMonth() - 5, 1);
            const params = {
                "startDate": startDate.getFullYear().toString() + String(startDate.getMonth() + 1).padStart(2, '0') + String(startDate.getDate()).padStart(2, '0'),
                "endDate": `${totalRequestDate.totalRequestDateY}${totalRequestDate.totalRequestDateM}${totalRequestDate.totalRequestEndDateD}`
            }
            const data = await CallApi.getAxiosAuthInfo(params);

            // 日毎のリクエスト回数を合計
            const grouped = {}
            data.req_infos.forEach(item => {
                const month = item.date.slice(0, 6);
                if (!grouped[month]) {
                    grouped[month] = { total: 0 };
                }
                grouped[month].total += Number(item.total_count || 0);
            })
            
            monthReqInfo.value = Object.entries(grouped).map(([month, val]) => ({
                month,
                ...val
            }));
        } catch (error) {
            console.error('リクエスト回数取得 通信エラー:', error);
        }

    }
    
    // 当月のリクエスト数を取得する
    const currentMonthInfo = computed(() => {
        const currentMonth = `${nowDate.getFullYear()}${String(nowDate.getMonth() + 1).padStart(2, '0')}`
        return monthReqInfo.value.find(item => item.month === currentMonth)
    })

    // 1か月の認証回数推移のグラフ描画
    const getMonthAuthCountChartData = () => {
        const labels = [];
        const data = [];

        // グラフのラベルとデータの作成
        for (let i = 5; i >= 0; i--) {
            const date = dayjs().subtract(i, 'month');
            const monthLabel = date.format('M月');

            labels.push(monthLabel);

            const key = date.format('YYYYMM');
            const found = monthReqInfo.value.find(m => m.month === key);

            data.push(found ? found.total : 0);
        }

        const chartData = {
            labels,
            datasets: [
                {
                    label: 'リクエスト数',
                    data,
                    backgroundColor: '#ff1493',
                }
            ]
        }
        return  chartData
    }
    const getMonthAuthCountChartOptions = () => {
        const chartOptions = { 
            responsive: true,
            maintainAspectRatio: false, 
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        boxWidth: 12,
                        boxHeight: 12,
                        pointStyle: 'rect',
                        usePointStyle: true
                    }
                },
                title: {
                    display: true,
                    text: '月間リクエスト数の推移',
                    align: 'start'
                }
            },
            scales: {
                x: {
                    grid: {
                        display: false
                    }
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: function(value) {
                            return Number.isInteger(value) ? value : '';
                        }
                    }
                }
            }
        }
        return chartOptions
    }
    // YYYY/MM/DD形式に変換
    const formatViewDate = (dateString)  => {
        if (!dateString) return "-"
        return `${dateString.slice(0, 4)}/${dateString.slice(4, 6)}/${dateString.slice(6, 8)}`
    }
    // 1000単位で,区切りにする表示形式(Ex,30,000)に変換
    // 値が取得できない、または数値でない場合は0を返す
    const formatViewContractNum = (price) => {
        const num = Number(price)
        return (Number.isNaN(num) ? 0 : num).toLocaleString()
    }

    // 契約情報を取得する
    const getContractInfo = async() => {
        try {
            const data = await CallApi.getAxiosContractPlan();
            contractInfo.contractStartDate = formatViewDate(data.contract_date_s);
            contractInfo.contractEndDate = formatViewDate(data.contract_date_e);
            contractInfo.contractPlanName = data.plan_name;
            contractInfo.monthPrice = formatViewContractNum(data.month_price);
            contractInfo.limitRegister = formatViewContractNum(data.limit_register);
            contractInfo.limitReqNum = formatViewContractNum(data.limit_reqnum);
        } catch (error) {
            console.error('契約情報取得 通信エラー:', error);
        }
    }

    // 値が0の場合は"-"表示にする
    const zeroToDash = (value) => {
        return value === '0' ? '-' : value
    }
    // 月額料金の表示(0円の場合は"-"表示)
    const monthPriceDisplay = computed(() => zeroToDash(contractInfo.monthPrice))
    // 登録人数上限の表示(0人の場合は"-"表示)
    const limitRegisterDisplay = computed(() => zeroToDash(contractInfo.limitRegister))
    // 全体リクエスト上限の表示(0リクエストの場合は"-"表示)
    const limitReqNumDisplay = computed(() => zeroToDash(contractInfo.limitReqNum))

    // プログレスバーの表示
    const requestRate = computed(() => {
        const total = Number(currentMonthInfo.value?.total || 0);
        const limit = Number(
            String(contractInfo?.limitReqNum || 0).replace(/,/g, '')
        );
        if (!limit) return 0;
        if(total == 0) return 0;
        const rate = (total / limit) * 100;
        if (!limit) return 0;
        return Math.min(rate, 100);
    });
    // バー表示用
    const progressBarRate = computed(() => {
        if (requestRate.value > 0 && requestRate.value < 1) {
            return 1;
        }
        return requestRate.value;
    });

    // パスワードの変更
    const changePass = async() =>{
        
        // パスワード一致確認
        if (changePassInfo.newPass1 !== changePassInfo.newPass2) {
            alert("新しいパスワードが一致しません。")
            return
        }
        // 長さチェック
        if (changePassInfo.newPass1.length < 8) {
            alert("パスワードは8文字以上で入力してください。")
            return
        }
        // バリデーション関数
        const isValidPassword = (password) => {
            return /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).+$/.test(password)
        }
        // 内容チェック
        if (!isValidPassword(changePassInfo.newPass1)) {
            alert("パスワードは英字・数字・記号をそれぞれ1文字以上含めてください")
            return
        }

        try{
            // Cognitoのパスワードを変更
            await Cognito.changeCognitoPass(changePassInfo.currentPass, changePassInfo.newPass1);
            alert('パスワードを変更しました')
            
        } catch (err) {
            console.log(err)
            switch (err.name) {
                case 'NotAuthorizedException':
                    alert('現在のパスワードが正しくありません');
                    break;
                case 'InvalidPasswordException': // API送信前に形式チェッは実施しているが、フェールオーバーとして記載
                    alert('パスワードの形式が不正です');
                    break;
                case 'LimitExceededException':
                    alert('試行回数が上限に達しました');
                    break;
                default:
                    alert('パスワードの変更に失敗しました:' + err)
            }
            
        } finally {
            changePassInfo.currentPass = "";
            changePassInfo.newPass1 = "";
            changePassInfo.newPass2 = "";
        }
    }

    onMounted(async () => {
        // ローディング画面の表示
        isLoading.value = true

        // 契約情報の取得
        await getContractInfo();
        // 認証リクエスト数の取得
        await getTotalRequest();

        // ローディング画面の停止
        isLoading.value = false;
    })

</script>
<template>
    <HeaderComponent></HeaderComponent>
    <NavigationBar></NavigationBar>
    <main>
        <div class="mainView">
            <div>
                <div class="titleArea">
                    <p class="title">利用確認</p>
                    <div class="titleTextArea">
                        <p class="titleText">ご利用状況の確認や、アカウント設定の管理ができます。</p>
                    </div>
                </div>
                <div class="viewArea">
                    <div class="viewUseStatusArea">
                        <div id="viewArea1">
                            <p class="viewUseStatusTitle"><span class="fontColorPk">① </span>月間リクエスト数</p>
                            <div id="monthReqArea">
                                <div id="thisMonthReqArea">
                                    <p>今月のリクエスト数</p>
                                    <p>
                                        <span id="totalRequestCount">{{ currentMonthInfo?.total || 0 }}</span> / 
                                        <span>{{ contractInfo.limitReqNum }}</span>
                                        <span> requests</span>
                                    </p>
                                    <div class="progressContainer">
                                        <div class="progress">
                                            <div
                                                class="progress-bar"
                                                :style="{ width: progressBarRate + '%' }"
                                            >
                                            </div>
                                        </div>
                                        <span class="progressRate">
                                            {{ Number(requestRate).toFixed(3) }}%
                                        </span>
                                    </div>
                                    <div class="termArea">
                                        <p class="termText">
                                            <img src="../assets/calendar.png" class="calendar-icon">
                                            集計期間 : 
                                            <span class="date">{{ getTotalRequestStartDate }}</span>～
                                            <span class="date">{{ getTotalRequestEndDate }}</span>
                                        </p>
                                    </div>
                                    
                                </div>
                                <div id="monthReqGrapheArea">
                                    <Bar :data="getMonthAuthCountChartData()" :options="getMonthAuthCountChartOptions()" />
                                </div>
                            </div>
                        </div>
                        <div id="viewArea2">
                            <p class="viewUseStatusTitle" ><span class="fontColorPk">② </span>契約プラン</p>
                            <div id="contractPlanArea">
                                <p class="contractPlanWeight"><span>{{ contractInfo.contractPlanName }}</span>{{ contractInfo.contractPlanName !== 'トライアル' ? 'コース' : '' }}</p>
                                <div id="monthPriceArea">
                                    <p><img src="../assets/userstatus_price.png" id="monthPriceImage">月額料金</p>
                                    <p><span class="contractPlanWeight">{{ monthPriceDisplay }}</span>{{ monthPriceDisplay !== '-' ? '円 (税抜)' : '' }} </p>
                                </div>
                                <div id="limitRegisterArea">
                                    <p><img src="../assets/userregist_img.png" id="limitRegisterImage">登録人数上限</p>
                                    <p><span class="contractPlanWeight">{{ limitRegisterDisplay }}</span>{{ limitRegisterDisplay !== '-' ? '人' : '' }}</p>
                                </div>
                                <div id="limitRequestArea">
                                    <p><img src="../assets/userstatus_req.png" id="limitRequestImage">全体リクエスト上限/月</p>
                                    <p><span class="contractPlanWeight">{{ limitReqNumDisplay }}</span>{{ limitReqNumDisplay !== '-' ? 'リクエスト' : '' }}</p>
                                </div>
                                <div id="contractTermArea">
                                    <p class="termText">契約期間 : <span>{{ contractInfo.contractStartDate }}</span> ～ <span>{{ contractInfo.contractEndDate }}</span></p>
                                    <button class="exeButton"  id="changePlanButton">プラン変更(準備中)</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="viewUseStatusArea">
                        <div id="viewArea3">
                            <p class="viewUseStatusTitle" ><span class="fontColorPk">③ </span>パスワード変更</p>
                            <div id="passChangeArea">
                                <div id="passChangeExeArea">
                                    <div id="passChangeTopArea">
                                        <div id="changePassImageArea">
                                            <img src="../assets/userstatus_changepass.png" id="changePassImage">
                                        </div>
                                        <div id="passChangeRightArea">                              
                                            <div id="inputPassChangeArea">
                                                <div>
                                                    <p class="inputBlock">現在のパスワード</p>
                                                    <input type="password" class="inputText" v-model="changePassInfo.currentPass" placeholder="現在のパスワードを入力">
                                                </div>
                                                <div>
                                                    <p class="inputBlock">新しいパスワード</p>
                                                    <input type="password" class="inputText" v-model="changePassInfo.newPass1" placeholder="新しいパスワードを入力">
                                                </div>
                                                <div>
                                                    <p class="inputBlock">新しいパスワード(確認)</p>
                                                    <input type="password" class="inputText" v-model="changePassInfo.newPass2" placeholder="新しいパスワードを再入力">
                                                </div>
                                            </div>
                                    
                                            <p>※パスワードは8文字以上で、英字・数字・記号をそれぞれ1文字以上含めてください。</p>
                                            <button class='nextButton' id="passChangebutton" @click="changePass">パスワードを変更する</button>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-box">
                <p>Loading...</p>
                <div class="spinner"></div>
            </div>
        </div>
    </main>

</template>

<style scoped>
    .mainView {
        margin-left: 25%;
        margin-right : 5%;
        background: #f9f7f7af;
        box-sizing: border-box;
    }
    .title{
        text-align: left;
        padding: 10px 0 0 20px;
        font-size: 25px;
        font-weight: bold;
    }
    .titleTextArea{
        margin:0 30px 30px 20px;
        justify-content: space-between;
        padding-bottom: 10px;
        border-bottom: 1px solid #cecbcb;
        display: flex;
    }
    .titleText{
        text-align: left;
        font-size: 20px;
    }
    .viewArea {
        padding: 20px 5% 20px 5%;
        width: 100%;
        box-sizing: border-box;
        margin-top:30px;
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
    .viewUseStatusArea {
        display: flex;
        justify-content: center;
        width: 100%;
        padding: 0 0 20px 0;
        gap: 24px;
    }
    #viewArea1, #viewArea2, #viewArea3{
        background: white;
        border-radius: 10px;
        border: 1px solid #969292;
        padding: 20px 10px 0;
    }
    #viewArea1{
        flex: 1.2;
    }
    #viewArea2{
        flex: 1;
    }
    #viewArea3{
        width:100%;
    }
    .viewUseStatusTitle {
        font-size: 18px;
        font-weight: bold;
        text-align: left;
        margin-bottom: 10px;
    }
    .viewUseStatusText {
        font-size: 18px;
        font-weight: bold;
        text-align: right;
    }
    
    #monthReqArea {
        display: flex;
        gap: 15px;
        align-items: stretch;
    }
    #thisMonthReqArea {
        display: flex;
        justify-content: center;
        border-right: 1px dotted #999;
        padding: 20px;
        flex-direction: column;

    }
    #thisMonthReqArea p{
        display: block;
        margin: 0 0 12px;
        text-align: left;
    }
    
    #totalRequestCount{
        color:#ff1493;
        font-weight: bold;
        font-size: 35px;
    }
    #monthReqGrapheArea {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px 10px 0 10px;
        min-height: 260px;
    }
    .progressContainer {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 8px 20px;
    }
    .progress {
        flex: 1;
        height: 20px;
        background: #e5e5e5;
        border-radius: 10px;
        position: relative;
        overflow: hidden;
    }
    .progress-bar {
        height: 100%;
        background: #ff1493;
        border-radius: 10px 0 0 10px;
    }
    .progressRate {
        flex-shrink: 0;
        white-space: nowrap;
        text-align: right;
        font-size: 14px;
    }
    .calendar-icon {
        width: 20px;
        height:20px;
        bottom:-10px;
    }
    .inputText{
        padding: 5px 0;
        border: 1px solid #969292;
        border-radius: 5px;
        background-color: white;
        text-align: left;
        width:100%;
        box-sizing:border-box;
        font-size: 12px;
    }
    #contractPlanArea{
        background: rgb(245, 229, 242);
        border-radius: 10px;
        padding:10px;  
        margin: 20px;     
    }
    #passChangeArea{
        display: flex;
        align-items: center;
        gap: 5px;
    }
    #passChangeExeArea{
        align-items: flex-start;
        text-align: left;
    }
    #changePassImageArea{
        padding: 50px 30px;
        flex-shrink: 0;
        width: 120px;
    }
    #changePassImage{
        width:100px;
        height: 100px;
    }
    #passChangeTopArea {
        display: flex;
        align-items: flex-start;
        gap: 40px;
        width: 100%;
        margin: 0 auto;
    }
    #passChangeRightArea {
        display: flex;
        flex: 1;
        flex-direction: column;
        width:100%;
        min-width:0;
    }
    #inputPassChangeArea{
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        width: 100%;
        justify-content: space-between;
        gap:10px;
        margin-bottom: 20px;
        flex-wrap:wrap;
    }
    #inputPassChangeArea > div {
        min-width: 0;
    }
    .inputBlock{
        display: flex;
        flex-direction: column;
        gap: 5px;
        width: 100%;
    }
    #passChangebutton{
        margin: 20px  0;
        width:40%;
    }
    #monthPriceArea, #limitRegisterArea, #limitRequestArea, #contractTermArea{
        display:flex;
        justify-content: space-between;
        flex: 1;
        margin: 30px 5px;
    }
    #monthPriceArea, #limitRegisterArea, #limitRequestArea
    {
        border-bottom: 1px solid #cecbcb;
    }
    .contractPlanWeight{
        font-weight:bold;
        text-align: left;
        font-size:20px;
    }
    .termArea{
        margin-top: auto;
    }
    .termText{
        color:gray;
    }
    #monthPriceImage, #limitRegisterImage, #limitRequestImage{
        width: 30px;
        height: 30px;
        margin:0 10px;
    }
    #changePlanButton{
        padding: 5px;
    }
    @media (max-width: 1400px){
        #inputPassChangeArea{
            grid-template-columns: repeat(2, 1fr);
        }
    }
    @media (max-width: 1280px) {
        #passChangeTopArea {
            flex-direction: column;
            gap: 20px;
        }
        #changePassImageArea {
            padding: 0;
            text-align: center;
        }
        #passChangeRightArea {
            width: 100%;
        }
        #inputPassChangeArea {
            flex-direction: column;
            gap: 15px;
        }
        #inputPassChangeArea > div {
            width: 100%;
        }
        .inputText {
            width: 100%;
            box-sizing: border-box;
        }
        #passChangebutton {
            width: 100%;
            width: 100%;
        }
    }
    @media (max-width: 1150px) {
        .mainView {
            margin-left: 0;
            margin-right: 0;
            padding: 16px;
        }
        #viewArea1,
        #viewArea2,
        #viewArea3 {
            width: 100%;
        }
        #passChangeArea {
            flex-direction: column;
            gap: 20px;
        }
        #passChangeTopArea {
            flex-direction: column;
        }
        #passChangeImageArea {
            padding: 20px 0;
            text-align: center;
        }
        #passChangebutton {
            width: 100%;
        }
        #contractTermArea,
        #monthPriceArea,
        #limitRegisterArea,
        #limitRequestArea {
            flex-direction: column;
            align-items: flex-start;
            margin: 16px 0;
        }
    }
    @media (max-width: 768px) {
        .viewUseStatusArea { 
            flex-direction: column;
        }
        #monthReqArea {
            flex-direction: column;
        }
        #thisMonthReqArea {
            width: 100%;
            box-sizing: border-box;
            border-right: none;
            border-bottom: 1px dotted #999;
            padding: 10px 20px 20px;
        }
        #monthReqGrapheArea {
            width: 100%;
            height: 220px;
        }
        #contractTermArea {
            align-items: center;
            text-align: center;
        }
        #monthPriceImage, #limitRegisterImage, #limitRequestImage{
            width: 22px;
            height: 22px;
            margin-right: 8px;
        }
 
        #changePlanButton {
            margin: 15px auto 0;
            display: block;
            font-size: 17px;
        }
        .contractPlanWeight{
            font-weight: 700;
            font-size: 24px;
            line-height: 1.2;
        }
        #contractPlanArea > p:first-child {
            text-align: center;
            font-size: 28px;
            margin-bottom: 25px;
        }
        #passChangeArea {
            gap: 16px;
        }
        .inputText {
            width: 100%;
        }
        #changePassImageArea {
            display: none;
        }
        #passChangeTopArea {
            display: flex;
            flex-direction: column;
        }
        #passChangeRightArea{
            width: 100%;
        }
        #inputPassChangeArea {
            display: flex;
            flex-direction: column;
            gap: 15px;
            width: 100%;
        }
        #inputPassChangeArea > div {
            width: 100%;
        }
        #inputPassChangeArea .inputText {
            width: 100%;
            box-sizing: border-box;
        }
        .viewUseStatusTitle {
            font-size: 20px;
        }
        .progressContainer {
            align-items: stretch;
        }
        .progress {
            width: 100%;
            min-width: 100px;
        }
        .progressRate {
            text-align: right;
        }
    }
     
    @media (max-width: 540px) {
        .title {
            font-size: 20px;
        }
        #passChangebutton {
            width: 100%;
        }
        .contractPlanWeight,
        .termText {
            font-size: 14px;
        }
        #viewArea1, #viewArea2, #viewArea3{
            min-width: 0;
            padding: 10px;
        }
        #changePlanButton {
            font-size: 13px;
        }
        .viewUseStatusTitle {
            font-size: 18px;
        }
    }
</style>